import React, { useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/styles";
import { Button, Container, Grid, Typography } from "@material-ui/core";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import { headRewrite } from "../function/headRewrite";

const useStyles = makeStyles((theme) => ({
  root: {
    background: "#eeeeee",
    color: "#111",
    minHeight: "100vh",
    margin: 0,
    padding: 0,
    fontFamily:
      '"Helvetica Neue", Arial, "Noto Sans JP", "Yu Gothic", sans-serif',
  },
  pageFrame: {
    background: "#eeeeee",
  },
  previewSwitch: {
    position: "sticky",
    top: 0,
    zIndex: 20,
    background: "rgba(238, 238, 238, 0.96)",
    backdropFilter: "blur(10px)",
    borderBottom: "1px solid #c8c8c8",
  },
  previewSwitchInner: {
    minHeight: 74,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(3),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5, 0),
    },
  },
  previewTitle: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(0.5),
  },
  previewSubTitle: {
    fontSize: 14,
    color: "#333",
  },
  previewButtons: {
    display: "flex",
    gap: theme.spacing(1.2),
    flexWrap: "wrap",
  },
  previewButton: {
    borderRadius: 0,
    border: "1px solid #111",
    boxShadow: "none",
    padding: theme.spacing(1.1, 2.2),
    fontWeight: 700,
    fontSize: 12,
    letterSpacing: "0.04em",
    "&:hover": {
      boxShadow: "none",
    },
  },
  previewButtonActive: {
    background: "#111",
    color: "#eeeeee",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
    },
  },
  previewButtonInactive: {
    background: "transparent",
    color: "#111",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
    },
  },
  hero: {
    padding: theme.spacing(8, 0, 10),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(6, 0, 7),
    },
  },
  section: {
    padding: theme.spacing(10, 0),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(7, 0),
    },
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    fontSize: 12,
    letterSpacing: "0.12em",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  labelLine: {
    flex: 1,
    height: 1,
    background: "#111",
    opacity: 0.7,
  },
  heroTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(30px, 4.7vw, 54px)",
    lineHeight: 1.22,
    letterSpacing: "-0.07em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  heading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(28px, 4vw, 48px)",
    lineHeight: 1.45,
    letterSpacing: "-0.05em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  subHeading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(22px, 2.6vw, 32px)",
    lineHeight: 1.5,
    letterSpacing: "-0.04em",
    fontWeight: 500,
    marginBottom: theme.spacing(2.5),
  },
  heroBody: {
    maxWidth: 650,
    fontSize: 15,
    lineHeight: 2.05,
    color: "#222",
    marginBottom: theme.spacing(4),
    whiteSpace: "pre-line",
  },
  body: {
    fontSize: 15,
    lineHeight: 2.1,
    color: "#222",
    whiteSpace: "pre-line",
  },
  noteBody: {
    fontSize: 14,
    lineHeight: 2,
    color: "#333",
    whiteSpace: "pre-line",
  },
  buttonRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(4),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      textAlign: "center",
    },
  },
  primaryButton: {
    background: "#111",
    color: "#eeeeee",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.6, 3.5),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#eeeeee",
      color: "#111",
      boxShadow: "none",
    },
  },
  textLink: {
    color: "#111",
    textDecoration: "none",
    borderBottom: "1px solid #111",
    paddingBottom: 4,
    fontSize: 14,
    fontWeight: 700,
  },
  keywordLine: {
    borderTop: "1px solid #111",
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    fontSize: 13,
    color: "#333",
  },
  heroVisual: {
    position: "relative",
    minHeight: 520,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      minHeight: 360,
      marginTop: theme.spacing(6),
    },
  },
  heroCircle: {
    position: "absolute",
    right: -40,
    bottom: 20,
    width: 260,
    height: 260,
    border: "1px solid #111",
    borderRadius: "50%",
    opacity: 0.65,
    [theme.breakpoints.down("sm")]: {
      width: 180,
      height: 180,
      right: -60,
    },
  },
  heroSquare: {
    position: "absolute",
    left: 20,
    top: 48,
    width: 180,
    height: 180,
    border: "1px solid #111",
    opacity: 0.55,
    [theme.breakpoints.down("sm")]: {
      width: 120,
      height: 120,
      left: 0,
    },
  },
  heroNumber: {
    position: "absolute",
    top: 20,
    right: 0,
    writingMode: "vertical-rl",
    fontSize: 13,
    letterSpacing: "0.12em",
    fontWeight: 700,
  },
  imageMock: {
    position: "relative",
    width: "72%",
    maxWidth: 390,
    minHeight: 420,
    border: "1px solid #111",
    background: "rgba(255, 255, 255, 0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    textAlign: "center",
    transform: "rotate(2deg)",
    [theme.breakpoints.down("sm")]: {
      width: "82%",
      minHeight: 290,
      maxWidth: 300,
    },
  },
  imageMockImage: {
    width: "100%",
    height: "100%",
    minHeight: 450,
    objectFit: "cover",
    display: "block",
    [theme.breakpoints.down("sm")]: {
      minHeight: 250,
    },
  },
  imageMockText: {
    fontSize: 12,
    lineHeight: 2,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#333",
  },
  thesisCard: {
    border: "1px solid #111",
    padding: theme.spacing(4),
    background: "transparent",
    minHeight: 280,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      minHeight: "auto",
    },
  },
  thesisLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  thesisText: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(21px, 2.4vw, 30px)",
    lineHeight: 1.7,
    letterSpacing: "-0.03em",
    fontWeight: 500,
  },
  card: {
    border: "1px solid #111",
    minHeight: 300,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "transparent",
    [theme.breakpoints.down("sm")]: {
      minHeight: 240,
    },
  },
  cardNumber: {
    fontSize: 24,
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: "0.02em",
    marginBottom: theme.spacing(2),
  },
  smallRule: {
    width: 32,
    height: 1,
    background: "#111",
    marginBottom: theme.spacing(3),
  },
  archiveList: {
    borderTop: "1px solid #111",
  },
  archiveListItem: {
    display: "grid",
    gridTemplateColumns: "72px 260px 1fr",
    gap: theme.spacing(3),
    padding: theme.spacing(3, 0),
    borderBottom: "1px solid #c8c8c8",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1.5),
    },
  },
  archiveListIndex: {
    fontSize: 14,
    fontWeight: 700,
    color: "#555",
  },
  archiveListTitle: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.6,
  },
  quoteBlock: {
    border: "1px solid #111",
    padding: theme.spacing(5),
    position: "relative",
    background: "#111",
    color: "#eeeeee",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(4, 3),
    },
  },
  quoteMark: {
    fontFamily: '"Times New Roman", serif',
    fontSize: 72,
    lineHeight: 0.8,
    opacity: 0.75,
    marginBottom: theme.spacing(2),
  },
  quoteText: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(22px, 3vw, 36px)",
    lineHeight: 1.7,
    letterSpacing: "-0.04em",
    fontWeight: 500,
  },
  experienceBox: {
    border: "1px solid #111",
    padding: theme.spacing(4),
    background: "transparent",
  },
  experienceItem: {
    borderTop: "1px solid #c8c8c8",
    padding: theme.spacing(2.2, 0),
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    fontSize: 15,
    lineHeight: 1.8,
    "&:first-child": {
      borderTop: "none",
      paddingTop: 0,
    },
  },
  experienceArrow: {
    fontWeight: 700,
  },
  imageGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  imagePanel: {
    minHeight: 360,
    border: "1px solid #111",
    background: "rgba(255, 255, 255, 0.16)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: theme.spacing(2.5),
  },
  imagePanelLink: {
    color: "inherit",
    textDecoration: "none",
    transition: "background 180ms ease, transform 180ms ease",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.36)",
      transform: "translateY(-2px)",
    },
  },
  imagePanelImage: {
    width: "100%",
    aspectRatio: "1 / 1",
    objectFit: "cover",
    border: "1px solid #111",
    marginBottom: theme.spacing(2.5),
    display: "block",
    background: "#eeeeee",
  },
  imagePanelLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },
  imagePanelTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 24,
    lineHeight: 1.45,
    letterSpacing: "-0.04em",
    marginBottom: theme.spacing(2),
  },
  imagePanelText: {
    fontSize: 14,
    lineHeight: 2,
    color: "#333",
  },
  audienceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    borderTop: "1px solid #c8c8c8",
    borderLeft: "1px solid #c8c8c8",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr 1fr",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  audienceItem: {
    borderRight: "1px solid #c8c8c8",
    borderBottom: "1px solid #c8c8c8",
    padding: theme.spacing(2.5),
  },
  audienceTitle: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: theme.spacing(1.2),
  },
  finalCta: {
    margin: theme.spacing(8, 0),
    border: "1px solid #111",
    padding: theme.spacing(7, 3),
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  finalTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(30px, 4.8vw, 58px)",
    lineHeight: 1.35,
    letterSpacing: "-0.05em",
    marginBottom: theme.spacing(2),
  },
  stripe: {
    position: "absolute",
    right: 20,
    top: 30,
    bottom: 30,
    width: 48,
    background:
      "repeating-linear-gradient(90deg, transparent 0, transparent 7px, #111 8px, #111 9px)",
    opacity: 0.6,
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
}));

const COLLECTIONS = {
  flower: {
    slug: "flower_existence",
    metaTitle:
      "COMME des GARÇONS HOMME PLUS 2022SS 花の存在｜Enclopedia Archive Study",
    metaDescription:
      "COMME des GARÇONS HOMME PLUS 2022SS『花の存在』を、素材の対比、色彩の流れ、コレクション全体の構成から読み解くEnclopediaのアーカイブ解説ページです。",
    metaKeywords:
      "COMME des GARCONS HOMME PLUS 2022SS,花の存在,コムデギャルソンオムプリュス,アーカイブファッション,コレクション解説,enclopedia,アンクロペディア",
    canonicalPath: "/archive_collection/comme_des_garcons_homme_plus_2022ss_flower_existence",
    heroImage: "media/collection/archive1.png",
    heroImageAlt:
      "COMME des GARÇONS HOMME PLUS 2022SS 花の存在のアーカイブコレクション画像",
    heroNumber: "01 / FLOWER",
    label: "ARCHIVE COLLECTION STUDY",
    title: "COMME des GARÇONS HOMME PLUS 2022SS『花の存在』",
    lead:
      "花を幸福の記号として消費するのではなく、美しさと儚さ、癒やしと痛みを同時に引き受ける存在として捉え直したコレクション。\nEnclopediaでは、花柄という分かりやすい視覚の奥にある素材構成とランウェイ全体の流れを読み解きます。",
    keywordLine: ["flower", "contrast", "military", "full bloom"],
    thesis:
      "このコレクションの核は、花の美しさと儚さを、素材・色彩・ショー全体の構成によって同時に見せた点にあります。",
    whyTitle: "花は、なぜここまで強い存在として現れたのか。",
    whyBody:
      "2022SSのHOMME PLUSでは、花は単なる装飾ではありません。異素材とぶつかり、無骨な服の構造へ入り込み、ショー全体の色彩変化の中で徐々に咲き切っていく。\nその見せ方を追うことで、『花の存在』というテーマが服の上でどう成立していたかが見えてきます。",
    pillars: [
      {
        number: "01",
        title: "FLOWER AS PRESENCE",
        text:
          "花を“飾り”として置くのではなく、服全体の印象を変える存在として扱う。花柄そのものがコレクションの主題へ押し上げられています。",
      },
      {
        number: "02",
        title: "MATERIAL CONTRAST",
        text:
          "白黒の花柄生地を黒いフェイクレザーに重ねることで、柔らかさと硬さが衝突する。花の繊細さは、その緊張の中でより鮮明になります。",
      },
      {
        number: "03",
        title: "RUNWAY AS BLOOM",
        text:
          "モノトーンからピンクを含む色彩へ。ショー全体が、花が咲き、満開へ向かう過程のように構成されているようにも見えます。",
      },
    ],
    readingTitle: "ルックとディテールから読む、花の美しさと儚さ。",
    readingIntro:
      "花の存在を理解するうえで重要なのは、“柄がある”ことではなく、“どこに、何と合わせて、どの順序で現れるか”を見ることです。",
    observations: [
      {
        index: "01",
        title: "モノトーンの花柄 × 黒いフェイクレザー",
        text:
          "柔らかな有機性を持つ花柄と、人工的で硬質なレザー感を並置することで、花は守られるのではなく、緊張の中で浮かび上がります。美しさが強まるほど、同時に壊れやすさも見えてくる構成です。",
      },
      {
        index: "02",
        title: "カーキのミリタリーを花柄で切り替える",
        text:
          "無骨さや機能性を担うミリタリーウェアに花柄を差し込むことで、花は弱い装飾ではなく、強い服の意味を変えてしまう存在になります。",
      },
      {
        index: "03",
        title: "モノトーンからピンクへ向かう色の流れ",
        text:
          "序盤は抑制された色から始まり、終盤にかけて花を象徴する色彩が強まっていく。満開は最も美しい瞬間であると同時に、終わりに最も近い瞬間でもある。その二重性がテーマと響き合います。",
      },
    ],
    quote:
      "花は、明るい場面だけのための記号ではない。だからこそこのコレクションでは、華やかさと儚さが同じ強度で立ち上がる。",
    perspectiveTitle: "Enclopediaは、このコレクションを“知識”と“体験”の両方から開く。",
    perspectiveCards: [
      {
        number: "01",
        title: "KNOWLEDGE",
        text:
          "なぜ花なのか。なぜフェイクレザーやミリタリーとぶつけるのか。単なる商品説明ではなく、テーマと服の関係まで可視化します。",
      },
      {
        number: "02",
        title: "EXPERIENCE",
        text:
          "柄の密度、素材の厚み、身体に乗せたときの緊張感は、画像だけでは掴みきれません。レンタルをコレクション体験として設計します。",
      },
      {
        number: "03",
        title: "COMMUNITY",
        text:
          "ギャルソンを詳しく知らなかった人も、花という入口から読み始められる。愛好家・学生・スタイリストが共有できる文脈を育てます。",
      },
    ],
    experienceTitle: "画像で見るだけでは終わらない。実物で、テーマを確かめる。",
    experienceBody:
      "Enclopediaでは、アーカイブを“所有前の試着”に閉じず、服を理解するための体験として扱います。\n花柄の密度、異素材の重さ、着用時の視線の動きまで含めて、このコレクションの魅力に触れられる導線を作ります。",
    experienceItems: [
      "花柄の密度と配置を実物で見る",
      "フェイクレザーとの素材差を触れて理解する",
      "コレクション背景を知ったうえでレンタルする",
    ],
    archiveItemsTitle: "このテーマを、実物で確かめる。",
    archiveItemsBody:
      "在庫導線には、単なる商品カードではなく、“このアイテムのどこを見るべきか”を添える想定です。知識の続きとして商品ページへ進める構成にします。",
    archiveItems: [
      {
        label: "ARCHIVE FOCUS 01",
        title: "Flower × Leather Contrast",
        productPath: "/product/34df6c1d-1b52-45c4-afc3-070b5a46c965",
        productImage:
          "media/product/1760259569.56001334df6c1d-1b52-45c4-afc3-070b5a46c965.jpg",
        text:
          "花柄と黒い質感の衝突を見るための導線。柔らかさが硬質さによって強調される構成を体験できます。",
      },
      {
        label: "ARCHIVE FOCUS 02",
        title: "Military × Floral Reconstruction",
        productPath: "/product/f9dc5b09-8bb5-4828-8d52-ffc553a96ace",
        productImage:
          "media/product/1730183983.8211675f9dc5b09-8bb5-4828-8d52-ffc553a96ace.jpg",
        text:
          "無骨な服の骨格へ花が入り込むことで、印象がどう変わるかを読み解く導線。",
      },
      {
        label: "ARCHIVE FOCUS 03",
        title: "Pink as Full Bloom",
        productPath: "/product/86db861c-951b-47ef-bf45-fc7c33fb2524",
        productImage:
          "media/product/1768918935.165018386db861c-951b-47ef-bf45-fc7c33fb2524.jpg",
        text:
          "ショー終盤の“開花のピーク”を想起させる、色彩と存在感に着目した導線。",
      },
    ],
  },
  orlando: {
    slug: "orlando",
    metaTitle:
      "COMME des GARÇONS HOMME PLUS 2020SS Orlando｜Enclopedia Archive Study",
    metaDescription:
      "COMME des GARÇONS HOMME PLUS 2020SS『Orlando』を、文学・時間・ジェンダーの視点から読み解くEnclopediaのアーカイブ解説ページです。",
    metaKeywords:
      "COMME des GARCONS HOMME PLUS 2020SS,Orlando,オーランドー,コムデギャルソンオムプリュス,アーカイブファッション,コレクション解説,enclopedia,アンクロペディア",
    canonicalPath: "/archive_collection/comme_des_garcons_homme_plus_2020ss_orlando",
    heroImage: "media/collection/archive2.png",
    heroImageAlt:
      "COMME des GARÇONS HOMME PLUS 2020SS Orlandoのアーカイブコレクション画像",
    heroNumber: "02 / ORLANDO",
    label: "ARCHIVE COLLECTION STUDY",
    title: "COMME des GARÇONS HOMME PLUS 2020SS『Orlando』",
    lead:
      "ヴァージニア・ウルフ『オーランドー』を起点に、時代・性・衣服の境界を横断したコレクション。\n古典的な服の形式を借りながら、それを固定された記号ではなく、変化し続ける身体のための装置として読み替えます。",
    keywordLine: ["literature", "transformation", "gender", "time"],
    thesis:
      "『Orlando』の重要性は、過去の様式を引用しながら、“男性服”“女性服”という見え方そのものを流動化した点にあります。",
    whyTitle: "服は、誰のものか。『Orlando』はその問いを曖昧にする。",
    whyBody:
      "2020SSのHOMME PLUSは、文学作品の世界観を単に引用するのではなく、服の形そのものへ翻訳しました。\n時代を越える身体、性別を横断する存在、固定されない自己。その感覚が、テーラリング、装飾、レイヤーの中に現れます。",
    pillars: [
      {
        number: "01",
        title: "LITERATURE INTO CLOTHES",
        text:
          "『オーランドー』の持つ時間の長さと変身の物語を、服のシルエットと重なりによって可視化する。",
      },
      {
        number: "02",
        title: "HISTORY IN COLLISION",
        text:
          "フロックコート、ドレス的な量感、現代的な軽さを同居させ、歴史衣装の再現ではなく、時代の衝突を作る。",
      },
      {
        number: "03",
        title: "BEYOND FIXED GENDER",
        text:
          "男性服と女性服を分けるコードをあえて揺らし、着る人の身体が一つの答えへ固定されない余白を残します。",
      },
    ],
    readingTitle: "ルックとディテールから読む、変化する身体のための服。",
    readingIntro:
      "『Orlando』は、装飾が多いから面白いのではありません。どの装飾が、どの歴史や身体感覚を横断しているかを見ることで、深度が変わります。",
    observations: [
      {
        index: "01",
        title: "古典的なテーラリングに、ドレス的な量感を接続",
        text:
          "肩や上半身には構築性が残る一方で、裾やレイヤーは流動的に広がる。男性服の秩序を保ちながら、その輪郭を別の身体へ開いています。",
      },
      {
        index: "02",
        title: "フリルや装飾を“役割”から自由にする",
        text:
          "装飾は性別を示す記号ではなく、時間の混線や視線の移動を作る要素として働く。飾りが分類を越えるための手段になります。",
      },
      {
        index: "03",
        title: "過去の様式と現代性を同時に置く",
        text:
          "歴史的なフォルムに、現代的な軽さやスピード感を重ねることで、物語の中で時間を越える主人公の感覚が服の上に立ち上がります。",
      },
    ],
    quote:
      "『Orlando』は、服を“誰のものか”で分けるのではなく、“どう変化できるか”で読み直すための提案です。",
    perspectiveTitle: "Enclopediaは、文学と服の間にある距離を、実物体験で縮める。",
    perspectiveCards: [
      {
        number: "01",
        title: "KNOWLEDGE",
        text:
          "文学、ショー、ブランドの意図をつなぎ、なぜ『Orlando』がHOMME PLUSで扱われたのかを可視化します。",
      },
      {
        number: "02",
        title: "EXPERIENCE",
        text:
          "着用すると、丈感、重心、レイヤーの揺れ方が、写真とは違う理解を生みます。服を考える体験へ変えます。",
      },
      {
        number: "03",
        title: "COMMUNITY",
        text:
          "ジェンダー、文学、歴史衣装、舞台芸術に関心のある人が自然に集まる文脈を作り、アーカイブへの入口を広げます。",
      },
    ],
    experienceTitle: "『Orlando』は、読むだけではなく、着ることで輪郭が変わる。",
    experienceBody:
      "画面上では装飾やシルエットとして見える差異が、実際に身体へ乗ると、丈感、重心、視線の集まり方として理解できます。\nEnclopediaでは、アーカイブを所有物ではなく、考え方を身体で辿るメディアとして扱います。",
    experienceItems: [
      "シルエットの重心を身体で知る",
      "装飾とテーラリングの共存を体感する",
      "背景を読んだ後にレンタルで確かめる",
    ],
    archiveItemsTitle: "このテーマを、実物で確かめる。",
    archiveItemsBody:
      "在庫導線には、Orlandoの世界観と接続できる視点を添え、単なる一覧ではなく、コレクション理解の続きを設計します。",
    archiveItems: [
      {
        label: "ARCHIVE FOCUS 01",
        title: "Tailoring Beyond Gender",
        productPath: "/product/8bf5cd8f-d952-4a04-b1c3-34fc46c6c2d5",
        productImage:
          "media/product/1727838682.84216768bf5cd8f-d952-4a04-b1c3-34fc46c6c2d5.png",
        text:
          "男性服の構築性を保ちながら、別の身体感覚へ開いていく一着を読む導線。",
      },
      {
        label: "ARCHIVE FOCUS 02",
        title: "Historical Form / Modern Energy",
        productPath: "/product/0f19e891-2910-4f0a-803d-a593a175ffbf",
        productImage:
          "media/product/1739200273.155390f19e891-2910-4f0a-803d-a593a175ffbf.jpg",
        text:
          "歴史的なフォルムと現在の軽さを同時に扱う、Orlandoらしい混線を示す導線。",
      },
      {
        label: "ARCHIVE FOCUS 03",
        title: "Dressing as Transformation",
        productPath: "/product/034d9b02-fe70-48fc-87af-808c691f88be",
        productImage:
          "media/product/1727830116.7223113034d9b02-fe70-48fc-87af-808c691f88be.png",
        text:
          "着ることで意味が変わる。体験の入口としてレンタルへつなぐ導線。",
      },
    ],
  },
  cdgLineGuide: {
    slug: "comme-des-garcons-line-guide",
    metaTitle:
      "コムデギャルソンのライン一覧と違い｜COMME des GARÇONS全ブランド完全ガイド",
    metaDescription:
      "コムデギャルソンのラインの違いを知りたい方へ。COMME des GARÇONS、COMME des GARÇONS HOMME PLUS、COMME des GARÇONS HOMME、COMME des GARÇONS HOMME DEUX、COMME des GARÇONS SHIRT、COMME des GARÇONS COMME des GARÇONS、PLAY COMME des GARÇONS、BLACK COMME des GARÇONS、JUNYA WATANABE、noir kei ninomiya、tao COMME des GARÇONSまで、各ラインの特徴・デザイナー・成り立ち・選び方を解説。Enclopediaでレンタルできるアーカイブ体験へ案内します。",
    metaKeywords:
      "コムデギャルソン ライン 違い,コムデギャルソン 種類,コムデギャルソン どんなブランド,COMME des GARCONS ライン,COMME des GARCONS 種類,COMME des GARCONS HOMME PLUS,コムデギャルソンオムプリュス,COMME des GARCONS HOMME,コムデギャルソンオム,COMME des GARCONS HOMME DEUX,コムデギャルソンオムドゥ,COMME des GARCONS SHIRT,COMME des GARCONS COMME des GARCONS,コムコム,PLAY COMME des GARCONS,BLACK COMME des GARCONS,JUNYA WATANABE COMME des GARCONS,JUNYA WATANABE MAN,noir kei ninomiya,tao COMME des GARCONS,COMME des GARCONS GIRL,コムデギャルソン レンタル,Enclopedia,アンクロペディア",
    canonicalPath: "/archive_collection/comme-des-garcons-line-guide",
    heroImage: "media/archive_fashion/archive3.png",
    heroImageAlt:
      "コムデギャルソンのライン一覧と違いを解説するEnclopediaアーカイブガイド画像",
    heroNumber: "03 / LINE GUIDE",
    label: "ARCHIVE BRAND GUIDE",
    title: "コムデギャルソンのライン一覧と違い",
    lead:
      "COMME des GARÇONSは、一つのブランド名で語られがちですが、実際には複数のラインとデザイナーによって構成される巨大なブランド群です。\nCOMME des GARÇONS HOMME PLUS、COMME des GARÇONS HOMME、COMME des GARÇONS HOMME DEUX、COMME des GARÇONS SHIRT、COMME des GARÇONS COMME des GARÇONS、PLAY COMME des GARÇONS、BLACK COMME des GARÇONS、JUNYA WATANABE、noir kei ninomiya、tao COMME des GARÇONS。\nそれぞれのラインは、価格帯や雰囲気が違うだけではありません。\n“どんな服を、誰に、どの距離感で届けるか”が違います。",
    keywordLine: [
      "line guide",
      "comme des garçons",
      "designer lines",
      "rental experience",
    ],
    thesis:
      "コムデギャルソンは、ラインごとの違いを知ると、“難しいブランド”ではなく、自分の入口を選べるブランドに変わります。",
    whyTitle: "コムデギャルソンが気になる。でも、ラインが多すぎて分からない。",
    whyBody:
      "コムデギャルソンを初めて調べると、多くの人が最初に混乱します。\nCOMME des GARÇONSとCOMME des GARÇONS HOMME PLUSは何が違うのか。COMME des GARÇONS HOMMEとCOMME des GARÇONS HOMME DEUXは別物なのか。PLAY COMME des GARÇONSは本格的なギャルソンなのか。JUNYA WATANABEやnoir kei ninomiyaはコムデギャルソンなのか。\nこのページでは、主要ラインの成り立ち、デザイナー、服の特徴、初めて着るならどこから入るべきかを整理します。",
    pillars: [
      {
        number: "01",
        title: "REI KAWAKUBO AS CENTER",
        text:
          "COMME des GARÇONSの中心には、川久保玲による“既存の美しさを疑う”姿勢があります。ブランド本体とCOMME des GARÇONS HOMME PLUSでは、その思想が最も強く表れます。",
      },
      {
        number: "02",
        title: "MANY DESIGNERS, MANY ENTRIES",
        text:
          "JUNYA WATANABE、noir kei ninomiya、tao COMME des GARÇONSなど、コムデギャルソンの中から生まれた才能は、それぞれ異なる角度から服を更新しています。",
      },
      {
        number: "03",
        title: "FROM DAILY TO RUNWAY",
        text:
          "COMME des GARÇONS SHIRTやPLAY COMME des GARÇONSのような入口に近いラインから、COMME des GARÇONS HOMME PLUSのようなランウェイ性の強いラインまで、体験の距離が大きく異なります。",
      },
    ],
    readingTitle: "主要ラインから読む、COMME des GARÇONSというブランド宇宙。",
    readingIntro:
      "ラインの違いを理解すると、コムデギャルソンは“全部難しい服”ではなく、日常に近い入口から強いコレクションピースまで、自分に合う距離で触れられるブランドだと分かります。",
    observations: [
      {
        index: "01",
        title: "COMME des GARÇONS",
        text:
          "Designer：川久保玲\nStarted：1969年\nブランドの中核。既存の美しさや服の普通を問い直す最もコンセプチュアルなラインです。身体を変形させる造形、黒、非対称、膨らみ、余白など、コムデギャルソンの思想が最も濃く表れます。\nおすすめ：コムデギャルソンを思想ごと知りたい人。アートピースのような服を体験したい人。",
      },
      {
        index: "02",
        title: "COMME des GARÇONS HOMME PLUS",
        text:
          "Designer：川久保玲\nStarted：1984年\nコムデギャルソン オムプリュスは、メンズコレクションにおける実験の中心です。テーラード、スーツ、パンツ、ミリタリー、装飾を大胆に再構成し、男性服の前提を揺さぶります。\nおすすめ：メンズのギャルソンらしさを強く体験したい人。ランウェイ性のある服を着てみたい人。",
      },
      {
        index: "03",
        title: "COMME des GARÇONS HOMME",
        text:
          "Designer：川久保玲 / 田中啓一 / 渡辺淳弥ほか\nStarted：1978年\nコムデギャルソン オムは、メンズの日常着に近いラインです。シャツ、ジャケット、パンツなどをベースにしながら、素材、柄、パッチワーク、シルエットにギャルソンらしさを残します。COMME des GARÇONS HOMME PLUSより生活に近い入口です。\nおすすめ：最初のコムデギャルソンとして、日常に取り入れやすいメンズ服を探している人。",
      },
      {
        index: "04",
        title: "COMME des GARÇONS HOMME DEUX",
        text:
          "Designer：川久保玲監修 / デザインチーム\nStarted：1987年\nコムデギャルソン オム ドゥは、スーツやジャケットを軸にしたテーラードラインです。ビジネスやフォーマルに近い形を保ちながら、生地、裏地、ボタン、シルエットに静かな違和感を入れます。\nおすすめ：仕事着やセットアップに近い距離でギャルソンを体験したい人。",
      },
      {
        index: "05",
        title: "COMME des GARÇONS SHIRT",
        text:
          "Designer：川久保玲監修 / デザインチーム\nStarted：1988年\nコムデギャルソン シャツは、シャツを中心にしたラインです。ストライプ、パッチワーク、切替、プリント、ポップな色使いが特徴で、ギャルソンの発想を軽やかに体験できます。\nおすすめ：初めての一着として、強すぎないけれど普通ではない服を着たい人。",
      },
      {
        index: "06",
        title: "COMME des GARÇONS COMME des GARÇONS",
        text:
          "Designer：川久保玲監修 / デザインチーム\nStarted：1993年ごろ\nコムデギャルソン コムデギャルソン、通称コムコム。ウィメンズの日常に近いラインで、フリル、丸襟、ギャザー、黒、ドット、チェックなどを用いながら、可愛さと違和感を共存させます。\nおすすめ：日常に近い距離で、ギャルソンのかわいさと癖を体験したい人。",
      },
      {
        index: "07",
        title: "PLAY COMME des GARÇONS",
        text:
          "Designer：川久保玲監修 / Filip Pagowskiによるハートロゴ\nStarted：2002年\nプレイ コムデギャルソンは、ハートロゴで知られる最も分かりやすい入口です。Tシャツ、カーディガン、シャツ、Converseコラボなどが中心で、ファッション初心者にも認知されやすいラインです。\nおすすめ：まずはロゴやベーシックなアイテムからコムデギャルソンに触れたい人。",
      },
      {
        index: "08",
        title: "BLACK COMME des GARÇONS",
        text:
          "Designer：川久保玲監修 / デザインチーム\nStarted：2009年\nブラック コムデギャルソンは、黒を軸に、過去のコムデギャルソン的要素を比較的手に取りやすい形で展開するラインです。ジャケット、シャツ、パンツなど、黒のギャルソンを入口として体験しやすいです。\nおすすめ：黒いギャルソンを着たいが、メインラインほど強すぎる服にはまだ抵抗がある人。",
      },
      {
        index: "09",
        title: "JUNYA WATANABE COMME des GARÇONS",
        text:
          "Designer：渡辺淳弥\nStarted：1992年\nジュンヤワタナベ コムデギャルソンは、パッチワーク、再構築、デニム、異素材、コラボレーションを得意とするデザイナーラインです。服をゼロから作るというより、既存の服を読み替え、別の服へ編集する感覚が強いです。\nおすすめ：デニム、ワーク、ミリタリー、コラボレーションからコムデギャルソンを知りたい人。",
      },
      {
        index: "10",
        title: "JUNYA WATANABE MAN",
        text:
          "Designer：渡辺淳弥\nStarted：2001年ごろ\nジュンヤワタナベ マンは、JUNYA WATANABEのメンズラインです。Levi's、THE NORTH FACE、Brooks Brothersなど、実在する服の歴史や機能を再編集するコラボレーションが多く、男性にも入りやすいラインです。\nおすすめ：ワークウェアやアウトドアを軸に、ひねりのある服を着たい人。",
      },
      {
        index: "11",
        title: "noir kei ninomiya",
        text:
          "Designer：二宮啓\nStarted：2012年\nノワール ケイ ニノミヤは、黒を軸に、チュール、ハーネス、スタッズ、リング、パーツ接続を用いて服を立体的に構築するブランドです。可愛い黒ではなく、黒を構造として見せます。\nおすすめ：黒、チュール、構築的なジャケットやドレスに興味がある人。",
      },
      {
        index: "12",
        title: "tao COMME des GARÇONS",
        text:
          "Designer：栗原たお\nStarted：2005年 / 2022年にtaoとして再始動\nタオ コムデギャルソンは、フリル、レース、パッチワーク、プリント、手仕事感のあるディテールが特徴です。強い前衛性よりも、日常の中に軽やかな違和感を入れるラインです。\nおすすめ：コムデギャルソンの中でも、軽さ、可愛さ、クラフト感のある服から入りたい人。",
      },
      {
        index: "13",
        title: "COMME des GARÇONS GIRL",
        text:
          "Designer：川久保玲監修 / デザインチーム\nStarted：2015年ごろ\nコムデギャルソン ガールは、少女性や制服的なムードを含みながら、ギャルソンらしい違和感を持つラインです。リボン、丸襟、チェック、フリルなど、分かりやすい可愛さの中に癖があります。\nおすすめ：可愛いギャルソン、若々しいギャルソンを探している人。",
      },
    ],
    quote:
      "コムデギャルソンは一つではない。だから、自分に合う入口が必ずある。",
    perspectiveTitle: "Enclopediaは、ラインの違いを“知識”から“体験”へ変える。",
    perspectiveCards: [
      {
        number: "01",
        title: "KNOWLEDGE",
        text:
          "COMME des GARÇONS HOMME PLUS、COMME des GARÇONS HOMME、COMME des GARÇONS SHIRTなど、名前だけでは分かりづらいラインの違いを整理します。",
      },
      {
        number: "02",
        title: "EXPERIENCE",
        text:
          "ラインの違いは、実際に着ると一気に分かります。強く見える服が意外と馴染むことも、普通に見える服に違和感が潜むこともあります。",
      },
      {
        number: "03",
        title: "ENTRY",
        text:
          "どのラインが自分に合うのかを、購入前にレンタルで確かめる。Enclopediaは、コムデギャルソンへの入口を所有ではなく体験から作ります。",
      },
    ],
    experienceTitle: "初めてなら、どのCOMME des GARÇONSから入るべきか。",
    experienceBody:
      "コムデギャルソンは、ラインによって入口がまったく違います。\n強いランウェイ性を求めるならCOMME des GARÇONS HOMME PLUS。日常に近いメンズならCOMME des GARÇONS HOMMEやCOMME des GARÇONS HOMME DEUX。軽く試すならCOMME des GARÇONS SHIRT、PLAY COMME des GARÇONS、BLACK COMME des GARÇONS。\nそして服の構造そのものを楽しみたいなら、JUNYA WATANABE、noir kei ninomiya、tao COMME des GARÇONSが入口になります。",
    experienceItems: [
      "強いギャルソンを体験したいならCOMME des GARÇONS HOMME PLUS",
      "日常に近いメンズならCOMME des GARÇONS HOMME / COMME des GARÇONS HOMME DEUX",
      "軽く試すならCOMME des GARÇONS SHIRT / PLAY COMME des GARÇONS / BLACK COMME des GARÇONS",
      "服の構造を楽しむならJUNYA WATANABE / noir kei ninomiya / tao COMME des GARÇONS",
    ],
    archiveItemsTitle: "ラインの違いを、実物で確かめる。",
    archiveItemsBody:
      "Enclopediaでは、COMME des GARÇONS HOMME PLUSを中心に、COMME des GARÇONS、JUNYA WATANABE、noir kei ninomiyaなど、ラインごとの違いを実物で体験できるアーカイブを扱っています。",
    archiveItems: [
      {
        label: "ARCHIVE FOCUS 01",
        title: "COMME des GARÇONS HOMME PLUS",
        productPath:
          "/search/COMME%20DES%20GARCONS%20HOMME%20PLUS/",
        productImage: "",
        text:
          "テーラード、ランウェイピース、シーズンテーマの強い一着を体験したい方へ。男性服の前提を揺さぶる、最も実験的な入口です。",
      },
      {
        label: "ARCHIVE FOCUS 02",
        title: "COMME des GARÇONS",
        productPath: "/search/COMME%20des%20GARCONS/",
        productImage: "",
        text:
          "ブランドの中核となる思想や造形を、実際の服で確かめたい方へ。黒、余白、非対称、身体の見え方を読む入口です。",
      },
      {
        label: "ARCHIVE FOCUS 03",
        title: "JUNYA WATANABE",
        productPath: "/search/JUNYA%20WATANABE/",
        productImage: "",
        text:
          "パッチワーク、再構築、ワークウェア、コラボレーションから入りたい方へ。既存の服をもう一度編集する面白さを体験できます。",
      },
    ],
  },
  maisonMargielaNumberTagGuide: {
    slug: "maison-margiela-number-tag-guide",
    metaTitle:
      "Maison Margielaタグ番号の意味｜マルジェラ数字タグ完全解説と買取ガイド",
    metaDescription:
      "Maison Margielaのタグ番号の意味を知りたい方へ。0 Artisanal、1ウィメンズ、4ワードローブ、6 MM6、10メンズ、14メンズワードローブ、22シューズに加え、1と10など複数番号に丸が付くタグ、番号プリントのない白タグまで解説。本人期・アーティザナル・Tabi・5ACなどの買取相談にもつながるEnclopediaの完全ガイドです。",
    metaKeywords:
      "マルジェラ タグ 番号,メゾンマルジェラ タグ 数字 意味,Maison Margiela 番号 意味,Maison Margiela タグ,マルジェラ 数字タグ,マルジェラ 白タグ,マルジェラ 真っ白 タグ,マルジェラ 番号なし タグ,マルジェラ 1 10 丸,マルジェラ 複数番号 丸,マルジェラ 0 意味,マルジェラ 1 意味,マルジェラ 4 意味,マルジェラ 6 意味,マルジェラ 10 意味,マルジェラ 14 意味,マルジェラ 22 意味,マルジェラ 10 14 違い,Maison Martin Margiela タグ,マルジェラ 本人期 タグ,マルジェラ アーティザナル,マルジェラ 買取,メゾンマルジェラ 買取,マルジェラ アーカイブ 買取,Enclopedia,アンクロペディア",
    canonicalPath: "/archive_collection/maison-margiela-number-tag-guide",
    heroImage: "media/archive_fashion/archive4.png",
    heroImageAlt:
      "Maison Margielaの数字タグとライン番号の意味を解説するEnclopediaアーカイブガイド画像",
    heroNumber: "04 / NUMBER TAG",
    label: "ARCHIVE BRAND GUIDE",
    title: "Maison Margiela タグ番号完全解説",
    lead:
      "Maison Margielaの白いタグに並ぶ、0から23までの数字。\n一見すると暗号のように見えますが、丸で囲まれた番号は、そのアイテムがどのラインやカテゴリーに属するかを示しています。\nただし、マルジェラのタグは単純な一覧表だけでは読み切れません。\n1と10のように複数番号に丸が付くもの、番号プリントがなく白く見えるタグのもの、旧Maison Martin Margiela期の仕様など、年代やラインによって例外も存在します。\nタグ番号を読めるようになると、その服が“ただのブランド品”ではなく、どの文脈にある一着なのかが見えてきます。",
    keywordLine: [
      "number tag",
      "maison margiela",
      "white tag",
      "buyback guide",
    ],
    thesis:
      "Maison Margielaの数字タグは、ラインを分類するための記号であると同時に、匿名性・構造・アーカイブ価値を読み解く入口です。",
    whyTitle:
      "マルジェラのタグ番号は、なぜここまで気になるのか。",
    whyBody:
      "Maison Margielaのタグは、ブランド名を大きく見せるためのものではありません。\n白い布、四隅のステッチ、0から23までの数字。その控えめな表示の中に、ブランドの匿名性と分類の思想が詰まっています。\nさらに、数字タグ導入以前の無地の白タグ、現在・近年品にも見られる番号プリントのない白タグ、複数番号に丸が付くタグ、0 10のようにArtisanalとメンズを横断する表示など、タグそのものが年代やラインを読む手がかりになります。\nただし、タグ仕様は例外も多いため、白タグ＝初期、白タグ＝高額、白タグ＝偽物とは断定できません。\n特に古着やアーカイブでマルジェラを探すとき、タグ番号は“どのラインの服なのか”“本人期やアーティザナルに近い文脈なのか”“メンズなのかワードローブなのか”を判断する入口になります。\nそして買取においても、タグ番号・年代・ライン・状態は査定の重要な情報になります。",
    pillars: [
      {
        number: "01",
        title: "NUMBER AS LINE",
        text:
          "丸で囲まれた番号は、そのアイテムが属するラインやカテゴリーを示します。0、1、4、6、10、14、22など、数字ごとに意味が異なります。",
      },
      {
        number: "02",
        title: "EXCEPTION AS CLUE",
        text:
          "複数番号に丸が付くタグ、0 10表記、番号プリントのない白タグなど、例外的なタグ仕様は年代やラインを読む重要な手がかりになります。",
      },
      {
        number: "03",
        title: "TAG AS VALUE CLUE",
        text:
          "買取やアーカイブ評価では、タグ番号だけで価値が決まるわけではありません。ただし、ライン・年代・アイテム種別を判断する入口として重要です。",
      },
    ],
    readingTitle:
      "0から23まで。Maison Margielaの数字タグを読む。",
    readingIntro:
      "数字タグは、単なるデザインではなく、Maison Margielaのライン構造を知るための地図です。ただし、白タグ、複数番号に丸が付くタグ、0 10表記など、例外も含めて読むことで、より実物に近い理解になります。",
    observations: [
      {
        index: "PRE",
        title:
          "番号プリントのない白タグ｜初期仕様だけでなく、近年品にも見られる例外タグ",
        text:
          "Maison Margielaのタグといえば、0から23までの数字が並んだ白タグを思い浮かべる人が多いですが、番号プリントがない、または表面上は真っ白に見える白タグも存在します。\n初期Maison Martin Margielaでは、数字タグ導入以前の無地タグが匿名性を象徴する重要な仕様として知られています。\n一方で、現在・近年のMaison Margielaでも、ランウェイピースや一部ライン、サンプル、仕様違いなどで無地に近い白タグが見られることがあります。\nそのため、白タグだから初期、白タグだから高額、白タグだから偽物、とは判断できません。\n買取視点：番号のない白タグは、年代・ライン・品質表示・縫製・素材・購入経路を合わせて確認する必要があります。LINE査定では、首元タグ、品質表示、全体写真、ディテール写真を必ず送るのがおすすめです。",
      },
      {
        index: "MIX",
        title:
          "複数番号に丸が付くタグ｜1と10、0と10など複数ラインを横断する表示",
        text:
          "Maison Margielaの数字タグでは、基本的に丸で囲まれた番号がラインを示します。ただし、アイテムによっては複数の番号に丸が付く場合があります。\nたとえばウィメンズとメンズの文脈を横断するようなもの、Artisanalとメンズを示す0 10のような表記など、単一番号だけでは説明しきれない仕様があります。\n買取視点：複数番号に丸がある場合は、通常のライン判定よりも慎重に見る必要があります。1と10が両方丸で囲まれている場合も、ユニセックス的な企画、ライン横断、年代特有の仕様、あるいは真贋確認が必要なケースまで考えられるため、タグ番号だけで即断せず、品質表示・縫製・素材・アイテム内容と合わせて確認します。",
      },
      {
        index: "00",
        title: "0｜Artisanal / アーティザナル",
        text:
          "手作業による再構築や一点物に近い作品を示す、Maison Margielaの中でも特にアーカイブ価値が語られやすい番号です。\n古着や既製服を解体し、別の服へ作り替える思想が強く表れます。\n買取視点：0番、0 10番、アーティザナル表記、再構築性の強い作品は、必ず写真付きで事前査定したい領域です。",
      },
      {
        index: "01",
        title: "1｜Women's Collection / ウィメンズコレクション",
        text:
          "Maison Margielaのウィメンズメインコレクションを示す番号です。\n解体、再構築、匿名性、テーラリング、ドレス的な表現など、ブランドの中核的な思想が現れます。\n買取視点：本人期・旧Maison Martin Margiela期の1番は、年代やデザインによってアーカイブ評価が変わります。",
      },
      {
        index: "02",
        title: "2｜Unassigned / 未使用・割当なし",
        text:
          "通常の主要ラインとしては明確な割当がない番号です。\nタグ上では数字として存在しますが、丸が付く主要カテゴリーとして見る機会は多くありません。\n買取視点：2番に丸がある場合は、真贋やタグ仕様を含めて慎重に確認した方がよいです。",
      },
      {
        index: "03",
        title: "3｜Fragrances / フレグランス",
        text:
          "香水・フレグランスを示す番号として整理されるカテゴリーです。\nMaison MargielaではReplicaフレグランスなど、香りの記憶をテーマにした展開も知られています。\n買取視点：衣類買取では主対象になりにくい番号ですが、ブランド全体のカテゴリー理解として押さえておくと便利です。",
      },
      {
        index: "04",
        title: "4｜Women's Wardrobe / ウィメンズワードローブ",
        text:
          "ウィメンズのワードローブ、日常着に近いラインを示す番号です。\nメインコレクションよりも生活に近い服として、ニット、ジャケット、シャツ、パンツなどに見られます。\n買取視点：派手なアーカイブではなくても、状態の良い定番品や需要のあるアイテムは査定対象になります。",
      },
      {
        index: "05",
        title: "5｜Unassigned / 未使用・割当なし",
        text:
          "通常の主要ラインとしては明確な割当がない番号です。\nタグ上には存在しますが、丸が付いて流通するアイテムは一般的ではありません。\n買取視点：5番のみが丸で囲まれている場合は、タグの整合性を確認する必要があります。",
      },
      {
        index: "06",
        title: "6｜MM6 Maison Margiela",
        text:
          "MM6 Maison Margielaを示す番号です。\nメインラインよりも日常的・コンテンポラリーな文脈が強く、Tシャツ、ワンピース、バッグ、シューズなど幅広く展開されます。\n買取視点：MM6も査定対象ですが、メインラインやアーティザナルとは評価軸が異なります。",
      },
      {
        index: "07",
        title: "7｜Unassigned / 未使用・割当なし",
        text:
          "通常の主要ラインとしては明確な割当がない番号です。\n数字タグの構造上は存在しますが、一般的な衣類カテゴリーとしては使われにくい番号です。\n買取視点：タグ番号とアイテム種別に違和感がある場合は、真贋確認が重要です。",
      },
      {
        index: "08",
        title: "8｜Eyewear / アイウェア",
        text:
          "アイウェアを示す番号として整理されます。\nサングラスや眼鏡など、衣類とは異なるアクセサリー領域のカテゴリーです。\n買取視点：アパレルと同じ基準ではなく、付属品・状態・型番・市場需要を確認します。",
      },
      {
        index: "09",
        title: "9｜Unassigned / 未使用・割当なし",
        text:
          "通常の主要ラインとしては明確な割当がない番号です。\n0から23まで数字は並びますが、すべての番号が常に主要ラインとして使われるわけではありません。\n買取視点：番号そのものより、タグ仕様・品質表示・縫製・アイテム内容の整合性を見る必要があります。",
      },
      {
        index: "10",
        title: "10｜Men's Collection / メンズコレクション",
        text:
          "Maison Margielaのメンズメインコレクションを示す番号です。\nジャケット、コート、シャツ、パンツ、ニットなど、メンズの中心的なラインとして古着市場でも見かけやすい番号です。\n買取視点：旧Maison Martin Margiela期、本人期、再構築性のある10番は、アーカイブとして丁寧に確認したい領域です。",
      },
      {
        index: "11",
        title: "11｜Accessories / アクセサリー",
        text:
          "アクセサリーを示す番号です。\nベルト、財布、バッグ、小物など、衣服以外のカテゴリーに関わる番号として理解できます。\n買取視点：状態、素材、付属品、需要のあるモデルかどうかを確認します。",
      },
      {
        index: "12",
        title: "12｜Fine Jewellery / ファインジュエリー",
        text:
          "ジュエリー関連のカテゴリーとして整理される番号です。\n衣類よりも素材・状態・付属品・販売証明などの確認が重要になります。\n買取視点：ジュエリー類は素材価値とブランド価値の両方を分けて見る必要があります。",
      },
      {
        index: "13",
        title: "13｜Objects & Publications / オブジェ・出版物",
        text:
          "オブジェや出版物など、服以外の表現を示す番号です。\nMaison Margielaは衣服だけでなく、空間、物、出版物を通じてもブランドの世界観を表現してきました。\n買取視点：希少なノベルティ、書籍、オブジェ類は、状態と流通量によって価値が変わります。",
      },
      {
        index: "14",
        title: "14｜Men's Wardrobe / メンズワードローブ",
        text:
          "メンズのワードローブラインを示す番号です。\n10番がメンズメインコレクションであるのに対し、14番はより日常的なメンズウェアに近い位置づけとして理解しやすい番号です。\n買取視点：10番と14番は混同されやすいため、タグ番号とアイテム内容をセットで見ることが重要です。",
      },
      {
        index: "15",
        title: "15｜Collaboration / 3 Suissesなど",
        text:
          "過去のコラボレーションや特定企画に関わる番号として語られることがあります。\n一般的な衣類ラインとして頻繁に見かける番号ではありません。\n買取視点：希少企画の場合は、タグ番号だけでなくコラボ内容や流通背景を確認します。",
      },
      {
        index: "16",
        title: "16｜Unassigned / 未使用・割当なし",
        text:
          "通常の主要ラインとしては明確な割当がない番号です。\n数字が存在するからといって、すべてが通常カテゴリーとして使われるわけではありません。\n買取視点：違和感のある番号表示は、タグ全体の整合性確認が必要です。",
      },
      {
        index: "17",
        title: "17｜Unassigned / 未使用・割当なし",
        text:
          "通常の主要ラインとしては明確な割当がない番号です。\nMaison Margielaの数字タグは一覧性が特徴ですが、未使用番号も含まれます。\n買取視点：番号だけで判断せず、品質タグや縫製、素材、年代を合わせて見ます。",
      },
      {
        index: "18",
        title: "18｜Unassigned / 未使用・割当なし",
        text:
          "通常の主要ラインとしては明確な割当がない番号です。\n古着で不自然な丸付き番号がある場合は注意が必要です。\n買取視点：番号とカテゴリーが合っているか、タグの形状・フォント・縫い付け方を確認します。",
      },
      {
        index: "19",
        title: "19｜Unassigned / Used in anonymous period",
        text:
          "一部では、匿名デザインチーム期に関わる番号として言及されることがあります。\nただし、一般ユーザーが最初に覚えるべき主要番号ではありません。\n買取視点：珍しい番号は、年代・タグ仕様・コレクション背景を含めて個別確認が必要です。",
      },
      {
        index: "20",
        title: "20｜Exhibitions / 展示・プロジェクト系",
        text:
          "展示やプロジェクトに関わる番号として整理されることがあります。\n通常の衣類ラインとは異なる文脈で理解した方がよい番号です。\n買取視点：資料性や限定性がある場合は、通常衣類とは別軸で確認します。",
      },
      {
        index: "21",
        title: "21｜Unassigned / 未使用・割当なし",
        text:
          "通常の主要ラインとしては明確な割当がない番号です。\n数字タグの一覧には含まれますが、頻繁に使われるカテゴリーではありません。\n買取視点：タグ番号だけで価値を決めず、アイテム全体を確認します。",
      },
      {
        index: "22",
        title: "22｜Shoes / シューズ",
        text:
          "シューズを示す番号です。\nTabiブーツ、スニーカー、ローファー、パンプスなど、Maison Margielaを象徴する靴カテゴリーと関係します。\n買取視点：Tabi、Replica、人気シューズは状態・サイズ・付属品・現行需要によって査定が大きく変わります。",
      },
      {
        index: "23",
        title: "23｜Unassigned / 未使用・割当なし",
        text:
          "通常の主要ラインとしては明確な割当がない番号です。\n0から23までの数字が並ぶことで、分類表のような匿名的な印象を作ります。\n買取視点：珍しい表示や違和感のあるタグは、真贋と年代の確認が必要です。",
      },
      {
        index: "FAQ",
        title: "白タグなら初期マルジェラ、または高額品と考えていい？",
        text:
          "そうとは限りません。\n番号プリントのない白タグは、初期Maison Martin Margielaを考えるうえで重要な仕様ですが、現在・近年のMaison Margielaにも無地に近い白タグが見られることがあります。\nそのため、白タグだけで年代や価値を判断するのは危険です。\nEnclopediaでは、白タグ、数字タグ、複数番号の丸付きタグを含め、品質表示、縫製、素材、アイテム内容、現在の需要まで見て査定します。",
      },
    ],
    quote:
      "Maison Margielaの数字タグは、ブランド名を叫ばない。だからこそ、知っている人には深く刺さる。",
    perspectiveTitle:
      "Enclopediaは、数字タグを“知識”から“買取相談”へつなげる。",
    perspectiveCards: [
      {
        number: "01",
        title: "KNOWLEDGE",
        text:
          "0、1、4、6、10、14、22などの番号を知ることで、Maison Margielaの服がどのラインに属するかを読み解けます。",
      },
      {
        number: "02",
        title: "ARCHIVE VALUE",
        text:
          "本人期、旧Maison Martin Margiela期、Artisanal、Tabi、5ACなどは、タグ番号・年代・状態を合わせて見ることで価値判断の精度が上がります。",
      },
      {
        number: "03",
        title: "BUYBACK",
        text:
          "タグ番号が分かる写真、品質表示、全体像、ディテールをLINEで送ることで、宅配買取へ進む前に事前査定の精度を高められます。",
      },
    ],
    experienceTitle:
      "マルジェラを売る前に、タグ番号を確認する。",
    experienceBody:
      "Maison Margielaの買取では、ブランド名だけでなく、タグ番号・年代・ライン・アイテム種別・状態が重要です。\n特に0番のArtisanal、1番のウィメンズメインライン、10番のメンズコレクション、14番のメンズワードローブ、22番のシューズは、査定時に確認したいポイントです。\n加えて、数字タグ導入以前の無地白タグ、現在・近年品にも見られる番号プリントのない白タグ、1と10のように複数番号に丸が付いているタグ、0 10表記などは、アーカイブ性や真贋確認の面でも重要です。\nただし、白タグや複数丸タグだけで価値が決まるわけではありません。タグ、品質表示、デザイン、状態、流通量を合わせて見ることで、売却前の相談がスムーズになります。",
    experienceItems: [
      "白い数字タグの丸付き番号を確認する",
      "1と10など、複数番号に丸が付いていないか確認する",
      "番号プリントのない白タグかどうか確認する",
      "品質表示タグ・品番・生産国を撮影する",
      "Tabiや5ACなどモデル名が分かる場合は伝える",
      "本人期・旧Maison Martin Margiela期と思われる場合は年代が分かる写真を送る",
    ],
    archiveItemsTitle:
      "Maison Margielaの価値を、買取相談へつなげる。",
    archiveItemsBody:
      "Enclopediaでは、Maison Margielaを単なるブランド名ではなく、タグ番号・ライン・年代・アーカイブ性まで見て査定します。売却前にLINEで写真を送ることで、宅配買取へ進む前におおまかな方向性を確認できます。",
    archiveItems: [
      {
        label: "BUYBACK FOCUS 01",
        title: "Maison Margiela買取ページへ",
        productPath: "/brand_buyback/maison-margiela",
        productImage: "",
        text:
          "本人期、アーティザナル、Tabi、5AC、八の字ライダース、MM6まで。Maison Margielaの買取対象と査定視点を詳しく確認できます。",
      },
      {
        label: "BUYBACK FOCUS 02",
        title: "LINEでマルジェラを事前査定",
        productPath: "https://line.me/R/ti/p/@240yzqkh",
        productImage: "",
        text:
          "数字タグ、品質表示、全体写真、ディテール写真を送るだけで、宅配買取へ進む前におおまかな査定方向を確認できます。",
      },
      {
        label: "BUYBACK FOCUS 03",
        title: "宅配買取を申し込む",
        productPath: "/kaitori/takuhai",
        productImage: "",
        text:
          "事前査定後、売却を進めたい場合は宅配買取へ。送料や流れを確認しながら、Maison MargielaをEnclopediaへお送りいただけます。",
      },
    ],
    finalCta: {
      title:
        "Maison Margielaのタグ番号を確認したら、次は価値を相談する。",
      body:
        "0番、10番、14番、22番。数字タグは、その一着の文脈を読む手がかりです。\n白タグ、複数番号の丸付きタグ、本人期、アーティザナル、Tabi、5AC、MM6まで、まずはLINEで写真をお送りください。",
      buttonLabel: "Maison MargielaをLINEで事前査定する",
      buttonHref: "https://line.me/R/ti/p/@240yzqkh",
    },
  },
};

const audiences = [
  ["愛好家", "ブランドの背景まで深く知りたい人"],
  ["スタイリスト", "作品づくりのために実物を探す人"],
  ["コレクター", "所有と体験の両方から服を見る人"],
  ["学生", "モードを学ぶ入口を探している人"],
  ["海外層", "日本のアーカイブ視点に触れたい人"],
];

const buildImageUrl = (path) => `${process.env.REACT_APP_IMAGE_URL || ""}${path}`;

function CollectionSeo({ collection }) {
  const canonicalUrl = `${typeof window !== "undefined" ? window.location.origin : ""}${collection.canonicalPath}`;
  const ogImage = collection.heroImage
    ? buildImageUrl(collection.heroImage)
    : `${process.env.PUBLIC_URL || ""}/favicon.ico`;

  useEffect(() => {
    headRewrite(collection.metaTitle, collection.metaDescription, collection.metaKeywords);
  }, [collection.metaTitle, collection.metaDescription, collection.metaKeywords]);

  return (
    <Helmet>
      <title>{collection.metaTitle}</title>
      <meta name="description" content={collection.metaDescription} />
      <meta name="keywords" content={collection.metaKeywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="Enclopedia" />
      <meta property="og:title" content={collection.metaTitle} />
      <meta property="og:description" content={collection.metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: collection.metaTitle,
          description: collection.metaDescription,
          mainEntityOfPage: canonicalUrl,
          publisher: {
            "@type": "Organization",
            name: "Enclopedia",
          },
          about: [
            "アーカイブファッション",
            "COMME des GARÇONS HOMME PLUS",
            collection.title,
          ],
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: typeof window !== "undefined" ? window.location.origin : "",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Archive Collection",
              item: `${typeof window !== "undefined" ? window.location.origin : ""}/archive_collection`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: collection.title,
              item: canonicalUrl,
            },
          ],
        })}
      </script>
    </Helmet>
  );
}

function ArchiveFocusCard({ item, classes }) {
  const content = (
    <div>
      {item.productImage && (
        <img
          src={buildImageUrl(item.productImage)}
          alt={item.title}
          className={classes.imagePanelImage}
        />
      )}
      <div className={classes.imagePanelLabel}>{item.label}</div>
      <Typography component="h3" className={classes.imagePanelTitle}>
        {item.title}
      </Typography>
      <Typography component="p" className={classes.imagePanelText}>
        {item.text}
      </Typography>
    </div>
  );

  if (item.productPath) {
    return (
      <a
        href={item.productPath}
        className={`${classes.imagePanel} ${classes.imagePanelLink}`}
        aria-label={`${item.title}の商品ページを見る`}
      >
        {content}
      </a>
    );
  }

  return <div className={classes.imagePanel}>{content}</div>;
}

function CollectionArchivePage({ collection }) {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <CollectionSeo collection={collection} />
      <Container maxWidth="xl">
        <div className={classes.pageFrame}>
          <section className={classes.hero}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={7}>
                <div className={classes.labelRow}>
                  <span>{collection.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography variant="h1" component="h1" className={classes.heroTitle}>
                  {collection.title}
                </Typography>
                <Typography component="p" className={classes.heroBody}>
                  {collection.lead}
                </Typography>
                <div className={classes.keywordLine}>
                  {collection.keywordLine.map((keyword, index) => (
                    <React.Fragment key={keyword}>
                      <span>{keyword}</span>
                      {index < collection.keywordLine.length - 1 && <span>/</span>}
                    </React.Fragment>
                  ))}
                </div>
                <div className={classes.buttonRow} style={{ marginTop: 32 }}>
                  <Button className={classes.primaryButton} component="a" href="https://www.enclopedia-official.com/">
                    レンタルできるアイテムを見る
                  </Button>
                  <a href="/help_guide" className={classes.textLink}>
                    Enclopediaの使い方
                  </a>
                </div>
              </Grid>
              <Grid item xs={12} md={5}>
                <div className={classes.heroVisual}>
                  <div className={classes.heroCircle} />
                  <div className={classes.heroSquare} />
                  <div className={classes.heroNumber}>{collection.heroNumber}</div>
                  <div className={classes.imageMock}>
                    <img
                      src={buildImageUrl(collection.heroImage)}
                      alt={collection.heroImageAlt}
                      className={classes.imageMockImage}
                    />
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>02 / WHY IT MATTERS</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {collection.whyTitle}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {collection.whyBody}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <div className={classes.thesisCard}>
                  <div>
                    <div className={classes.thesisLabel}>KEY THESIS</div>
                    <Typography component="p" className={classes.thesisText}>
                      {collection.thesis}
                    </Typography>
                  </div>
                  <div className={classes.keywordLine}>
                    <span>knowledge</span>
                    <span>/</span>
                    <span>collection</span>
                    <span>/</span>
                    <span>experience</span>
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>03 / THREE POINTS</span>
              <span className={classes.labelLine} />
            </div>
            <Grid container spacing={3}>
              {collection.pillars.map((pillar) => (
                <Grid item xs={12} md={4} key={pillar.number}>
                  <div className={classes.card}>
                    <div>
                      <div className={classes.cardNumber}>{pillar.number}</div>
                      <div className={classes.cardTitle}>{pillar.title}</div>
                      <div className={classes.smallRule} />
                      <Typography component="p" className={classes.body}>
                        {pillar.text}
                      </Typography>
                    </div>
                  </div>
                </Grid>
              ))}
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>04 / HOW TO READ</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {collection.readingTitle}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {collection.readingIntro}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <div className={classes.archiveList}>
                  {collection.observations.map((item) => (
                    <div className={classes.archiveListItem} key={item.index}>
                      <div className={classes.archiveListIndex}>{item.index}</div>
                      <div className={classes.archiveListTitle}>{item.title}</div>
                      <Typography component="p" className={classes.noteBody}>
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.quoteBlock}>
              <div className={classes.quoteMark}>“</div>
              <Typography component="p" className={classes.quoteText}>
                {collection.quote}
              </Typography>
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>05 / ENCLOPEDIA PERSPECTIVE</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {collection.perspectiveTitle}
            </Typography>
            <Grid container spacing={3}>
              {collection.perspectiveCards.map((card) => (
                <Grid item xs={12} md={4} key={card.number}>
                  <div className={classes.card}>
                    <div>
                      <div className={classes.cardNumber}>{card.number}</div>
                      <div className={classes.cardTitle}>{card.title}</div>
                      <div className={classes.smallRule} />
                      <Typography component="p" className={classes.body}>
                        {card.text}
                      </Typography>
                    </div>
                  </div>
                </Grid>
              ))}
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6} alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>06 / EXPERIENCE</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {collection.experienceTitle}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {collection.experienceBody}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <div className={classes.experienceBox}>
                  {collection.experienceItems.map((item) => (
                    <div className={classes.experienceItem} key={item}>
                      <span>{item}</span>
                      <span className={classes.experienceArrow}>→</span>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>07 / ARCHIVE FOCUS</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {collection.archiveItemsTitle}
            </Typography>
            <Typography component="p" className={classes.heroBody}>
              {collection.archiveItemsBody}
            </Typography>
            <div className={classes.imageGrid}>
              {collection.archiveItems.map((item) => (
                <ArchiveFocusCard item={item} classes={classes} key={item.label} />
              ))}
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>08 / AUDIENCE</span>
              <span className={classes.labelLine} />
            </div>
            <div className={classes.audienceGrid}>
              {audiences.map(([title, text]) => (
                <div className={classes.audienceItem} key={title}>
                  <div className={classes.audienceTitle}>{title}</div>
                  <Typography component="p" className={classes.noteBody}>
                    {text}
                  </Typography>
                </div>
              ))}
            </div>
          </section>

          <div className={classes.finalCta}>
            <div className={classes.stripe} />
            <Typography component="p" className={classes.finalTitle}>
              アーカイブを、読むだけで終わらせない。
            </Typography>
            <Typography component="p" className={classes.heroBody} style={{ margin: "0 auto 32px" }}>
              コレクションの背景を知り、実物に触れ、服を体験として理解するための入口です。
            </Typography>
            <Button className={classes.primaryButton} component="a" href="/">
              アイテムを探す
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
}

function ArchiveCollectionIndex() {
  const classes = useStyles();
  const collections = useMemo(() => Object.values(COLLECTIONS), []);

  useEffect(() => {
    headRewrite(
      "Archive Collection Study｜Enclopedia",
      "Enclopediaが扱うブランドアーカイブの各コレクション解説一覧ページです。",
      "archive collection,enclopedia,アーカイブファッション,コレクション解説"
    );
  }, []);

  return (
    <div className={classes.root}>
      <Helmet>
        <title>Archive Collection Study｜Enclopedia</title>
        <meta
          name="description"
          content="Enclopediaが扱うブランドアーカイブの各コレクション解説一覧ページです。"
        />
        <link
          rel="canonical"
          href={`${typeof window !== "undefined" ? window.location.origin : ""}/archive_collection`}
        />
      </Helmet>
      <Container maxWidth="xl">
        <section className={classes.hero}>
          <div className={classes.labelRow}>
            <span>ARCHIVE COLLECTION STUDY</span>
            <span className={classes.labelLine} />
          </div>
          <Typography variant="h1" component="h1" className={classes.heroTitle}>
            コレクション解説
          </Typography>
          <Typography component="p" className={classes.heroBody}>
            ブランドの各コレクションを、背景・ルック・素材・体験導線から読み解くためのページです。
          </Typography>
        </section>
        <section className={classes.section}>
          <div className={classes.archiveList}>
            {collections.map((collection, index) => (
              <a
                href={collection.canonicalPath}
                className={classes.archiveListItem}
                key={collection.canonicalPath}
                style={{ color: "inherit", textDecoration: "none" }}
              >
                <div className={classes.archiveListIndex}>
                  {String(index + 1).padStart(2, "0")}
                </div>
                <div className={classes.archiveListTitle}>{collection.title}</div>
                <Typography component="p" className={classes.noteBody}>
                  {collection.metaDescription}
                </Typography>
              </a>
            ))}
          </div>
        </section>
      </Container>
    </div>
  );
}

const findCollectionBySlug = (slug) => {
  if (!slug) return null;
  return Object.values(COLLECTIONS).find((collection) => {
    const canonicalSlug = collection.canonicalPath.split("/").filter(Boolean).pop();
    return collection.slug === slug || canonicalSlug === slug;
  });
};

export default function ArchiveCollectionScreen() {
  const { slug } = useParams();
  const collection = useMemo(() => findCollectionBySlug(slug), [slug]);

  if (!slug) {
    return <ArchiveCollectionIndex />;
  }

  if (!collection) {
    return (
      <div style={{ padding: 48 }}>
        <Helmet>
          <title>Archive Collection Not Found｜Enclopedia</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <Typography component="h1" variant="h1">
          Archive collection not found
        </Typography>
      </div>
    );
  }

  return <CollectionArchivePage collection={collection} />;
}
